import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ArrowLeft, ChevronDown, ChevronUp, Delete, X, Scale, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import goldIcon from '../assets/gold-icon.png';
import silverIcon from '../assets/silver-icon.png';
import platinumIcon from '../assets/platinum-icon.png';

// Types
type MetalType = 'gold' | 'silver';
type Currency = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD' | 'CNY' | 'HKD' | 'SGD' | 'AED' | 'SAR' | 'INR' | 'EGP' | 'BTC' | 'ETH';
type Unit = 'oz' | 'g' | 'tola';

interface MetalRow {
  id: string;
  metal: MetalType;
  purityLabel: string;
  purityValue: number;
  weight: string;
  unit: Unit;
}

// Constants
const CURRENCIES: Record<Currency, { symbol: string; rate: number; icon: string }> = {
  USD: { symbol: '$', rate: 1.0, icon: 'https://flagcdn.com/w40/us.png' },
  EUR: { symbol: '€', rate: 0.92, icon: 'https://flagcdn.com/w40/eu.png' },
  GBP: { symbol: '£', rate: 0.79, icon: 'https://flagcdn.com/w40/gb.png' },
  CAD: { symbol: 'C$', rate: 1.35, icon: 'https://flagcdn.com/w40/ca.png' },
  AUD: { symbol: 'A$', rate: 1.53, icon: 'https://flagcdn.com/w40/au.png' },
  CNY: { symbol: '¥', rate: 7.23, icon: 'https://flagcdn.com/w40/cn.png' },
  HKD: { symbol: 'HK$', rate: 7.80, icon: 'https://flagcdn.com/w40/hk.png' },
  SGD: { symbol: 'S$', rate: 1.35, icon: 'https://flagcdn.com/w40/sg.png' },
  AED: { symbol: 'AED', rate: 3.67, icon: 'https://flagcdn.com/w40/ae.png' },
  SAR: { symbol: 'SAR', rate: 3.75, icon: 'https://flagcdn.com/w40/sa.png' },
  INR: { symbol: '₹', rate: 83.5, icon: 'https://flagcdn.com/w40/in.png' },
  EGP: { symbol: 'E£', rate: 48.5, icon: 'https://flagcdn.com/w40/eg.png' },
  BTC: { symbol: '₿', rate: 0.000015, icon: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png' },
  ETH: { symbol: 'Ξ', rate: 0.0003, icon: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' },
};

const UNITS: Record<Unit, { label: string; short: string; toOzt: number }> = {
  oz: { label: 'Ounce', short: 'OUNCE', toOzt: 1.0 },
  g: { label: 'Gram', short: 'GRAM', toOzt: 0.0321507 },
  tola: { label: 'Tola', short: 'tola', toOzt: 0.375 },
};

const METAL_CONFIG: Record<MetalType, { label: string; color: string; image: string; purities: { label: string; value: number }[] }> = {
  gold: {
    label: 'Gold',
    color: 'text-yellow-400',
    image: goldIcon,
    purities: [
      { label: '24K', value: 1.0 },
      { label: '22K', value: 0.9167 },
      { label: '21K', value: 0.875 },
      { label: '18K', value: 0.750 },
      { label: '14K', value: 0.5833 },
    ],
  },
  silver: {
    label: 'Silver',
    color: 'text-gray-300',
    image: silverIcon,
    purities: [
      { label: '999', value: 0.999 },
      { label: '925', value: 0.925 },
    ],
  },
};

// Initial Rows Generator
const generateInitialRows = (): MetalRow[] => {
  const rows: MetalRow[] = [];
  (Object.keys(METAL_CONFIG) as MetalType[]).forEach((metal) => {
    METAL_CONFIG[metal].purities.forEach((purity) => {
      rows.push({
        id: `${metal}-${purity.value}-${Math.random().toString(36).substr(2, 9)}`,
        metal,
        purityLabel: purity.label,
        purityValue: purity.value,
        weight: '',
        unit: 'oz', // Default unit per row
      });
    });
  });
  return rows;
};

const isMarketOpen = (date: Date) => {
  const nyTime = new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const day = nyTime.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const hour = nyTime.getHours();
  const month = nyTime.getMonth(); // 0 = Jan, 1 = Feb
  const dateNum = nyTime.getDate();

  // Helper to check if it's the Nth weekday of the month
  const isNthWeekday = (n: number, weekday: number) => {
    const firstDay = new Date(nyTime.getFullYear(), month, 1).getDay();
    const offset = (weekday - firstDay + 7) % 7;
    const firstOccurrence = 1 + offset;
    const targetDate = firstOccurrence + (n - 1) * 7;
    return dateNum === targetDate;
  };

  // Holiday Early Closes (Usually 2:00 PM ET)
  // MLK Day (3rd Mon in Jan), Presidents' Day (3rd Mon in Feb), Memorial Day (Last Mon in May),
  // Juneteenth (Jun 19), Independence Day (Jul 4), Labor Day (1st Mon in Sep)
  let isHoliday = false;
  
  if (month === 0 && day === 1 && isNthWeekday(3, 1)) isHoliday = true; // MLK
  if (month === 1 && day === 1 && isNthWeekday(3, 1)) isHoliday = true; // Presidents
  if (month === 5 && dateNum === 19) isHoliday = true; // Juneteenth
  if (month === 6 && dateNum === 4) isHoliday = true; // Independence
  if (month === 8 && day === 1 && isNthWeekday(1, 1)) isHoliday = true; // Labor
  
  // Memorial Day (Last Mon in May)
  if (month === 4 && day === 1) {
     const nextWeek = new Date(nyTime);
     nextWeek.setDate(dateNum + 7);
     if (nextWeek.getMonth() !== month) isHoliday = true;
  }

  // On these holidays, market usually closes at 2:00 PM ET and reopens at 6:00 PM ET
  if (isHoliday && hour >= 14 && hour < 17) return false;

  // Thanksgiving (4th Thu in Nov) - Early close 1:00 PM ET
  if (month === 10 && day === 4 && isNthWeekday(4, 4) && hour >= 13 && hour < 17) return false;

  // Christmas (Dec 25) & New Year (Jan 1) - Full Close or Early Close depending on year, simplified to closed
  if ((month === 11 && dateNum === 25) || (month === 0 && dateNum === 1)) return false;

  // Weekend closure
  if (day === 6) return false; // Saturday
  if (day === 5 && hour >= 17) return false; // Friday after 5 PM
  if (day === 0 && hour < 18) return false; // Sunday before 6 PM

  // Daily break (Mon-Thu) 5 PM - 6 PM
  if (day >= 1 && day <= 4 && hour === 17) return false;

  return true;
};

const getNextOpenTime = (from: Date) => {
  const curr = new Date(from);
  // Reset minutes/seconds to 0 to align with hourly checks
  curr.setMinutes(0);
  curr.setSeconds(0);
  curr.setMilliseconds(0);
  
  // Look ahead hour by hour up to 7 days
  for (let i = 0; i < 168; i++) {
    curr.setHours(curr.getHours() + 1);
    if (isMarketOpen(curr)) {
      return curr;
    }
  }
  return null;
};

const getCountdownText = (now: Date) => {
  const nextOpen = getNextOpenTime(now);
  if (!nextOpen) return "";
  
  const diff = nextOpen.getTime() - now.getTime();
  if (diff <= 0) return "Opening soon";

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) return `Opens in ${days}d ${hours}h`;
  if (hours > 0) return `Opens in ${hours}h ${minutes}m`;
  return `Opens in ${minutes}m`;
};

const MetalsCalculator: React.FC = () => {
  // State
  const [rows, setRows] = useState<MetalRow[]>(() => {
    const saved = localStorage.getItem('metal_rows');
    return saved ? JSON.parse(saved) : generateInitialRows();
  });
  const [currency, setCurrency] = useState<Currency>(() => {
    const saved = localStorage.getItem('metal_currency');
    return (saved as Currency) || 'USD';
  });
  
  const [currencyRates, setCurrencyRates] = useState<Record<Currency, number>>({
    USD: 1.0,
    EUR: 0.92,
    GBP: 0.79,
    CAD: 1.35,
    AUD: 1.53,
    CNY: 7.23,
    HKD: 7.80,
    SGD: 1.35,
    AED: 3.67,
    SAR: 3.75,
    INR: 83.5,
    EGP: 48.5,
    BTC: 0.000015,
    ETH: 0.0003,
  });

  // Persistence Effects
  useEffect(() => {
    localStorage.setItem('metal_rows', JSON.stringify(rows));
  }, [rows]);

  useEffect(() => {
    localStorage.setItem('metal_currency', currency);
  }, [currency]);

  const [spotPrices, setSpotPrices] = useState<Record<MetalType, number>>({
    gold: 5400.00,
    silver: 116.00,
  });
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isAutoUpdate, setIsAutoUpdate] = useState(true);
  const [expandedMetals, setExpandedMetals] = useState<Record<MetalType, boolean>>({
    gold: true,
    silver: true,
  });
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [openUnitRowId, setOpenUnitRowId] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchCurrencyRates = useCallback(async () => {
    try {
      // Try CoinGecko first for real-time rates (using USDT as proxy for USD)
      // This provides much fresher data than standard free currency APIs
      const geckoRes = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=tether&vs_currencies=usd,eur,gbp,cad,cny,hkd,sgd,aed,sar,inr,egp,btc,eth,aud');
      
      if (geckoRes.ok) {
        const geckoData = await geckoRes.json();
        if (geckoData.tether) {
          const rates = geckoData.tether;
          const usdRate = rates.usd || 1;
          
          setCurrencyRates(prev => {
            const newRates = { ...prev };
            (Object.keys(prev) as Currency[]).forEach(c => {
              const key = c.toLowerCase();
              if (rates[key]) {
                newRates[c] = rates[key] / usdRate;
              }
            });
            return newRates;
          });
          return; // Success, skip fallback
        }
      }
      throw new Error("CoinGecko data missing");
    } catch (e) {
      console.warn("Real-time currency fetch failed, falling back to daily rates", e);
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        if (data.rates) {
          setCurrencyRates(prev => {
            const newRates = { ...prev };
            (Object.keys(prev) as Currency[]).forEach(c => {
              if (data.rates[c]) {
                newRates[c] = data.rates[c];
              }
            });
            return newRates;
          });
        }
      } catch (error) {
        console.error("Failed to fetch currency rates", error);
      }
    }
  }, []);

  // Fetch currency rates on mount
  useEffect(() => {
    fetchCurrencyRates();
  }, [fetchCurrencyRates]);

  // Fetch Live Prices
  const fetchLivePrices = useCallback(async (manual = false) => {
    if (manual) setIsUpdating(true);
    try {
      const [goldRes, silverRes] = await Promise.all([
        fetch('https://api.gold-api.com/price/XAU'),
        fetch('https://api.gold-api.com/price/XAG')
      ]);

      const goldData = await goldRes.json();
      const silverData = await silverRes.json();

      if (goldData.price && silverData.price) {
        setSpotPrices(prev => ({
          ...prev,
          gold: goldData.price,
          silver: silverData.price,
        }));
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error("Failed to fetch gold/silver prices", error);
    } finally {
      if (manual) setIsUpdating(false);
    }
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAutoUpdate) {
      fetchLivePrices(false);
      interval = setInterval(() => fetchLivePrices(false), 5000);
    }
    return () => clearInterval(interval);
  }, [isAutoUpdate, fetchLivePrices]);

  // Handlers
  const handleKeypadPress = (key: string) => {
    if (!activeRowId) return;
    
    // Handle Row Operations
    if (key === 'ADD_ROW') {
      const currentRowIndex = rows.findIndex(r => r.id === activeRowId);
      if (currentRowIndex === -1) return;
      
      const currentRow = rows[currentRowIndex];
      const newRow: MetalRow = {
        ...currentRow,
        id: `${currentRow.metal}-${currentRow.purityValue}-${Math.random().toString(36).substr(2, 9)}`,
        weight: '',
        unit: currentRow.unit 
      };
      
      const newRows = [...rows];
      newRows.splice(currentRowIndex + 1, 0, newRow);
      setRows(newRows);
      setActiveRowId(newRow.id);
      return;
    }

    if (key === 'DEL_ROW') {
      const currentRow = rows.find(r => r.id === activeRowId);
      if (!currentRow) return;

      // Check if it's the last one of this type
      const siblings = rows.filter(r => r.metal === currentRow.metal && r.purityValue === currentRow.purityValue);
      
      if (siblings.length > 1) {
        const newRows = rows.filter(r => r.id !== activeRowId);
        setRows(newRows);
        setActiveRowId(null); 
      } else {
        // Just clear the value
        setRows(prev => prev.map(row => row.id === activeRowId ? { ...row, weight: '' } : row));
      }
      return;
    }

    setRows(prev => prev.map(row => {
      if (row.id !== activeRowId) return row;
      
      if (key === 'UNIT') {
        const unitKeys = Object.keys(UNITS) as Unit[];
        const currentIndex = unitKeys.indexOf(row.unit);
        const nextIndex = (currentIndex + 1) % unitKeys.length;
        return { ...row, unit: unitKeys[nextIndex] };
      }

      let newWeight = row.weight;
      if (key === 'C') {
        newWeight = '';
      } else if (key === 'BACK') {
        newWeight = newWeight.slice(0, -1);
      } else if (key === '.') {
        if (!newWeight.includes('.')) newWeight += key;
      } else {
        newWeight += key;
      }
      return { ...row, weight: newWeight };
    }));
  };

  // Keyboard Support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!activeRowId) return;

      if (e.key >= '0' && e.key <= '9') {
        handleKeypadPress(e.key);
      } else if (e.key === '.') {
        handleKeypadPress('.');
      } else if (e.key === 'Backspace') {
        handleKeypadPress('BACK');
      } else if (e.key === 'Delete') {
        handleKeypadPress('C');
      } else if (e.key === 'Escape' || e.key === 'Enter') {
        setActiveRowId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const handleUnitChange = (id: string, newUnit: Unit) => {
    setRows(prev => prev.map(row => row.id === id ? { ...row, unit: newUnit } : row));
  };

  const toggleMetal = (metal: MetalType) => {
    setExpandedMetals(prev => ({ ...prev, [metal]: !prev[metal] }));
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to erase all data? This cannot be undone.')) {
      localStorage.removeItem('metal_rows');
      localStorage.removeItem('metal_currency');
      setRows(generateInitialRows());
      setCurrency('USD');
      setActiveRowId(null);
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val * currencyRates[currency]);
  };

  const calculateRowValue = (row: MetalRow) => {
    const weight = parseFloat(row.weight) || 0;
    const weightInOzt = weight * UNITS[row.unit].toOzt;
    return weightInOzt * row.purityValue * spotPrices[row.metal];
  };

  const totalValue = rows.reduce((sum, row) => sum + calculateRowValue(row), 0);
  const formattedTotal = formatCurrency(totalValue);
  
  const getTotalFontSize = (val: string) => {
    if (val.length > 15) return 'text-3xl md:text-4xl';
    if (val.length > 10) return 'text-4xl md:text-5xl';
    return 'text-5xl md:text-6xl';
  };

  const activeMetal = openUnitRowId ? rows.find(r => r.id === openUnitRowId)?.metal : null;

  return (
    <div className="min-h-screen bg-zinc-950 md:flex md:justify-center md:items-center font-sans">
      <div className="w-full max-w-md bg-black h-[100dvh] md:h-[850px] md:rounded-[3rem] md:border-[8px] md:border-zinc-900 overflow-hidden relative shadow-2xl flex flex-col">
        
        {/* Scrollable Content Area */}
        <div className={`flex-1 overflow-y-auto no-scrollbar transition-all duration-300 ${activeRowId ? 'pb-[380px]' : 'pb-[120px]'}`}>
          {/* Header / Display */}
          <div className="bg-black sticky top-0 z-[60] pt-safe-top">
            
            {/* Logo at the top left */}
            <div className="px-6 pt-6 pb-4 flex justify-between items-center">
              <img 
                src={logo} 
                alt="ValueMyMetals" 
                className="h-5 object-contain opacity-90"
                style={{ filter: 'brightness(0.4)' }}
              />
              <button 
                onClick={() => fetchLivePrices(true)}
                className={`text-zinc-600 hover:text-white transition-colors p-2 -mr-2 duration-300 ${isUpdating ? 'animate-spin' : 'active:rotate-180'}`}
                title="Refresh Prices"
                disabled={isUpdating}
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pb-6 text-right">
              <div className="text-zinc-500 text-sm font-medium mb-1 uppercase tracking-wide">Total Value</div>
              <div 
                onClick={handleReset}
                className={`${getTotalFontSize(formattedTotal)} font-light tracking-tight text-white transition-all duration-300 cursor-pointer active:text-red-500 ${isUpdating ? 'animate-pulse opacity-50' : ''}`}
                title="Click to reset all data"
              >
                {formattedTotal}
              </div>
              <div className="text-xs text-zinc-600 mt-2 font-medium flex items-center justify-end gap-2">
                {isMarketOpen(lastUpdated) ? (
                  <>
                    <span>Market Open</span> • {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </>
                ) : (
                  <>
                    <span>Market Closed</span> • {getCountdownText(lastUpdated)}
                  </>
                )}
              </div>
            </div>
            
            {/* Settings Bar */}
            <div className="px-6 pb-4 flex justify-end gap-3 relative">
                <button 
                  onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                  className="bg-zinc-900 text-white text-sm rounded-full px-4 py-2 border border-zinc-800 flex items-center gap-2 font-medium hover:bg-zinc-800 transition-colors"
                >
                  <img 
                    src={CURRENCIES[currency].icon} 
                    alt={currency} 
                    className="w-5 h-5 object-contain rounded-full"
                  />
                  {currency}
                  <ChevronDown className="w-4 h-4 text-zinc-500" />
                </button>

                {isCurrencyOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsCurrencyOpen(false)} />
                    <div className="absolute right-6 top-full mt-2 w-40 bg-zinc-900 border border-zinc-800 rounded-xl shadow-xl overflow-hidden z-50 max-h-64 overflow-y-auto">
                      {Object.keys(CURRENCIES).map((c) => (
                        <div 
                          key={c}
                          onClick={() => {
                            setCurrency(c as Currency);
                            setIsCurrencyOpen(false);
                          }}
                          className={`px-4 py-3 flex items-center gap-3 hover:bg-zinc-800 cursor-pointer transition-colors ${currency === c ? 'bg-zinc-800/50' : ''}`}
                        >
                          <img 
                            src={CURRENCIES[c as Currency].icon} 
                            alt={c} 
                            className="w-5 h-5 object-contain rounded-full"
                          />
                          <span className="text-white text-sm font-medium">{c}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
            </div>
          </div>

          {/* Scrollable List */}
          <div className="px-4 space-y-3">
            {(Object.keys(METAL_CONFIG) as MetalType[]).map((metal) => (
              <div key={metal} className={`bg-zinc-900/50 rounded-2xl border border-zinc-800/50 backdrop-blur-sm relative ${activeMetal === metal ? 'z-10' : 'z-0'}`}>
                <div 
                  className="px-5 py-3 flex items-center justify-between cursor-pointer active:bg-zinc-800 transition-colors"
                  onClick={() => toggleMetal(metal)}
                >
                  <span className={`font-semibold text-xl ${METAL_CONFIG[metal].color}`}>{METAL_CONFIG[metal].label}</span>
                  {expandedMetals[metal] ? <ChevronUp className="w-5 h-5 text-zinc-500" /> : <ChevronDown className="w-5 h-5 text-zinc-500" />}
                </div>

                {expandedMetals[metal] && (
                  <div className="divide-y divide-zinc-800/50">
                    {rows.filter(r => r.metal === metal).map((row) => (
                      <div 
                        key={row.id} 
                        className={`px-5 py-4 flex items-center justify-between transition-all cursor-pointer ${activeRowId === row.id ? 'bg-zinc-800 ring-1 ring-orange-500/50' : 'hover:bg-zinc-800/30'}`}
                        onClick={() => setActiveRowId(row.id)}
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <img 
                            src={METAL_CONFIG[row.metal].image} 
                            alt={row.metal} 
                            className="w-10 h-10 mr-4 rounded-full object-cover bg-black shadow-sm flex-shrink-0" 
                          />
                          <span className="text-base font-medium text-zinc-300 truncate">{row.purityLabel}</span>
                        </div>
                        
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-2">
                            <div className={`text-2xl font-light ${activeRowId === row.id ? 'text-orange-400' : 'text-white'}`}>
                              {row.weight || <span className="text-zinc-700">0</span>}
                            </div>
                            
                            {/* Per-Row Unit Selector - Enhanced Visibility */}
                            <div className="relative" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => setOpenUnitRowId(openUnitRowId === row.id ? null : row.id)}
                                className="bg-zinc-800 text-white text-xs rounded-lg px-3 py-1.5 border border-zinc-600 focus:outline-none font-bold cursor-pointer hover:bg-zinc-700 hover:border-zinc-500 transition-all uppercase tracking-wider shadow-sm min-w-[4rem] text-center"
                              >
                                {UNITS[row.unit].short}
                              </button>

                              {openUnitRowId === row.id && (
                                <>
                                  <div className="fixed inset-0 z-40" onClick={() => setOpenUnitRowId(null)} />
                                  <div className="absolute right-0 top-full mt-1 w-24 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl overflow-hidden z-[70]">
                                    {Object.entries(UNITS).map(([key, u]) => (
                                      <div
                                        key={key}
                                        onClick={() => {
                                          handleUnitChange(row.id, key as Unit);
                                          setOpenUnitRowId(null);
                                        }}
                                        className={`px-3 py-2 text-xs font-bold text-white hover:bg-zinc-800 cursor-pointer transition-colors uppercase ${row.unit === key ? 'bg-zinc-800/50' : ''}`}
                                      >
                                        {u.short}
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-sm text-zinc-400 font-medium">
                            {row.weight ? formatCurrency(calculateRowValue(row)) : '-'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Footer Info */}
            <div className="pt-8 pb-4 text-center space-y-4">
              <p className="text-zinc-400 text-xs">
                Tip: Click on Total Value to erase all entries instantly.
              </p>
              
              <div className="flex justify-center">
                <a href='https://ko-fi.com/N4N51HEL7N' target='_blank' rel="noopener noreferrer">
                  <img height='36' style={{border:'0px', height:'36px'}} src='https://storage.ko-fi.com/cdn/kofi3.png?v=6' alt='Buy Me a Coffee at ko-fi.com' />
                </a>
              </div>

              <div className="text-zinc-600 text-sm flex flex-col gap-2">
                <a href="mailto:info@valuemymetals.app" className="hover:text-zinc-400 transition-colors">info@valuemymetals.app</a>
                <span>© {new Date().getFullYear()} ValueMyMetals</span>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Keypad (Absolute Bottom) */}
        <div className="fixed md:absolute bottom-0 left-0 right-0 w-full max-w-md mx-auto bg-black/90 backdrop-blur-xl border-t border-zinc-800 p-4 pb-6 z-50 rounded-t-[2rem] shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
          <div className="max-w-md mx-auto">
            {activeRowId ? (
               <div className="grid grid-cols-4 gap-2">
                {['7', '8', '9'].map(key => (
                  <button
                    key={key}
                    onClick={() => handleKeypadPress(key)}
                    className="h-12 rounded-full bg-zinc-800 text-white text-xl font-medium active:bg-zinc-700 transition-colors"
                  >
                    {key}
                  </button>
                ))}
                <button
                  onClick={() => handleKeypadPress('BACK')}
                  className="h-12 rounded-full bg-zinc-800 text-white flex items-center justify-center active:bg-zinc-700 transition-colors"
                >
                  <Delete className="w-6 h-6" />
                </button>

                {['4', '5', '6'].map(key => (
                  <button
                    key={key}
                    onClick={() => handleKeypadPress(key)}
                    className="h-12 rounded-full bg-zinc-800 text-white text-xl font-medium active:bg-zinc-700 transition-colors"
                  >
                    {key}
                  </button>
                ))}
                <button
                  onClick={() => handleKeypadPress('C')}
                  className="h-12 rounded-full bg-zinc-300 text-black text-lg font-semibold active:bg-white transition-colors"
                >
                  AC
                </button>

                {['1', '2', '3'].map(key => (
                  <button
                    key={key}
                    onClick={() => handleKeypadPress(key)}
                    className="h-12 rounded-full bg-zinc-800 text-white text-xl font-medium active:bg-zinc-700 transition-colors"
                  >
                    {key}
                  </button>
                ))}
                
                {/* Split Action Button */}
                <div className="row-span-2 flex flex-col gap-2">
                  <button
                    onClick={() => handleKeypadPress('ADD_ROW')}
                    className="flex-1 rounded-2xl bg-blue-600 text-white flex items-center justify-center active:bg-blue-500 transition-colors"
                  >
                    <Plus className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => handleKeypadPress('DEL_ROW')}
                    className="flex-1 rounded-2xl bg-red-900/50 text-red-400 flex items-center justify-center active:bg-red-900 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                <button
                  onClick={() => handleKeypadPress('0')}
                  className="col-span-1 h-12 rounded-full bg-zinc-800 text-white text-xl font-medium active:bg-zinc-700 transition-colors"
                >
                  0
                </button>
                <button
                  onClick={() => handleKeypadPress('.')}
                  className="h-12 rounded-full bg-zinc-800 text-white text-xl font-medium active:bg-zinc-700 transition-colors"
                >
                  .
                </button>
                <button
                  onClick={() => setActiveRowId(null)}
                  className="h-12 rounded-full bg-orange-500 text-white flex items-center justify-center active:bg-orange-400 transition-colors"
                >
                  <ChevronDown className="w-8 h-8" />
                </button>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-zinc-400 text-sm font-medium">Select an item to enter weight</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetalsCalculator;